import PropTypes from 'prop-types';
import prepareTranslate from '@/helpers/dictionary';
import {
  TextInput,
  Button,
  Grid,
  Select,
  NumberInput,
} from '@mantine/core';
import validations from '@/helpers/validation';
import { useForm } from '@mantine/form';
import companyQuery from '@/api/queries/company';
import productQuery from '@/api/queries/product';
import { ACCORD_TYPE, PRODUCT_ENTITY } from '@/helpers/enums';
import Modal from '@/components/DetailsModal';
import { useDisclosure } from '@mantine/hooks';

function ClientEditForm(props) {
  const {
    client, onCancel, onSave, onDelete, opened, close, isLoading,
  } = props;
  const t = prepareTranslate();

  const { data: companiesData } = companyQuery.useGetAll();
  const { data: productsData } = productQuery.useGetAll();
  const [deleteOpened, { open: deleteOpen, close: deleteClose }] = useDisclosure();

  const companiesOptions = companiesData?.map((company) => ({
    label: company.hebrew,
    value: company._id,
  })) || [];

  // eslint-disable-next-line max-len
  const clientProducts = productsData?.filter((product) => product.applicableTo.includes(PRODUCT_ENTITY.client));
  const productsOptions = clientProducts?.map((product) => ({
    label: product.hebrew,
    value: product._id,
  })) || [];

  const form = useForm({
    initialValues: {
      clientNumber: client.clientNumber || '',
      uid: client.uid || '',
      accordType: client.accordType || 'independent',
      agentCommission: client.agentCommission || '',
      employeeAgentCommission: client.employeeAgentCommission || '',
      companyId: client.companyId._id.toString() || '',
      branchId: client.branchId._id.toString() || '',
      clientName: client.clientName || '',
      comments: client.comments || '',
    },
    validate: {
      companyId: (value) => validations.required(value),
      uid: (value) => validations.required(value),
      accordType: (value) => validations.required(value),
      branchId: (value) => validations.required(value),
      clientNumber: (value) => validations.required(value),
    },
  });

  const clientAccordType = form.values.accordType;

  const handleSubmit = () => {
    onSave(form.values);
  };

  const handleDeleteAgreement = () => {
    if (!form.values.deletionReason) {
      form.setFieldError('deletionReason', 'שדה חובה');
      return;
    }

    onDelete(form.values.deletionReason);
    deleteClose();
  };

  const handleCancel = () => {
    deleteClose();
  };

  const deleteActions = [
    <Button key='delete' w={'6rem'} onClick={handleDeleteAgreement}>
      {t('global.delete')}
    </Button>,
    <Button key='cancel' w={'6rem'} onClick={handleCancel} variant='outline'>
      {t('global.cancel')}
    </Button>,
  ];

  const modalActions = [
    <Button key='save' loading={isLoading} onClick={handleSubmit}>
      {t('global.update')}
    </Button>,
    <Button key='cancel' variant='outline' onClick={onCancel}>
      {t('global.cancel')}
    </Button>,
  ];

  return (
    <Modal
      opened={opened}
      close={close}
      label={t('components.viewAgentClients.edit')}
      actions={modalActions}
    >
      <Grid gutter={{ base: 'xl' }}>
        <Grid.Col span={{ base: 3 }}>
          <Select
            label={t('components.editClients.company')}
            key={form.key('companyId')}
            data={companiesOptions}
            required
            {...form.getInputProps('companyId')}
          />
          <TextInput
            autoComplete='off'
            maxLength={25}
            required
            label={t('components.editClients.uid')}
            key={form.key('uid')}
            {...form.getInputProps('uid')}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 3 }}>
          <Select
            required
            label={t('components.editClients.branch')}
            data={productsOptions}
            key={form.key('branchId')}
            {...form.getInputProps('branchId')}
          />
        <Select
            required
            label={t('components.editClients.accordType')}
            key={form.key('accordType')}
            defaultValue={ACCORD_TYPE[1].value}
            data={ACCORD_TYPE}
            {...form.getInputProps('accordType')}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 3 }}>
          <TextInput
            autoComplete='off'
            label={t('components.editClients.number')}
            key={form.key('clientNumber')}
            maxLength={7}
            required
            {...form.getInputProps('clientNumber')}
          />
           {clientAccordType === 'independent' ? (
            <NumberInput
              autoComplete='off'
              label={t('components.editClients.commission')}
              key={form.key('agentCommission')}
              {...form.getInputProps('agentCommission')}
            />
           ) : (
            <NumberInput
              autoComplete='off'
              label={t('components.editClients.commission')}
              key={form.key('employeeAgentCommission')}
              {...form.getInputProps('employeeAgentCommission')}
            />
           )}
        </Grid.Col>

        <Grid.Col span={{ base: 3 }}>
          <TextInput
            label={t('components.editClients.clientName')}
            key={form.key('clientName')}
            {...form.getInputProps('clientName')}
          />

        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col>
          <TextInput
            label={t('components.editClients.comments')}
            key={form.key('comments')}
            maxLength={300}
            {...form.getInputProps('comments')}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col>
          <Button variant='outline' onClick={deleteOpen}>
            {t('components.editClients.deleteClient')}
          </Button>
        </Grid.Col>
      </Grid>
            <Modal
              opened={deleteOpened}
              close={handleCancel}
              label={t('components.editClients.verifyDeletion')}
              actions = {deleteActions}
            >
              <TextInput
                required
                {...form.getInputProps('deletionReason')}
                label= {t('components.editClients.deletionReason')}
              />
              </Modal>
    </Modal>
  );
}

ClientEditForm.propTypes = {
  client: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  opened: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default ClientEditForm;
